import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrSelectOption } from '@app/components/pr-select/pr-select.component';
import { API_ROUTE_ORGANIZATION_MEMBER_GLOBAL } from '@app/dao/organization-members/api-router';
import { PledgeDao } from '@app/dao/pledge/pledge.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { ProductsTypeEnum } from '@app/forms/subscription/definition/interface';
import { ProductGlobalItem } from '@app/forms/subscription/definition/interface/product';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { environment } from '@environments/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  map,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { API_ROUTE_PRODUCT } from '../../api/api-router';
import {
  PledgeLegalEntityChoices,
  PledgeRead,
  PrevoyanceDevisData,
} from '../../definition/interface/Pledge';
import { FormControlOption } from '../../definition/interface/formControlOption.interface';
import { ProductSearchItem } from '../../definition/interface/product';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { mapProductType, reverseMapProductType } from '../../services/utile';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';
import {
  LegalEntityChoicesLoader,
  LegalEntitySelection,
  attachedLegalEntityOptions,
  defaultLegalEntitySelection,
  selectSellerLegalEntity,
  sellerLegalEntityOptions,
  showsAttachedLegalEntitySelect,
  attachedLegalEntityOwnerLabel,
  showsSellerLegalEntitySelect,
  toLegalEntityInitParams,
} from './legal-entity-choice';
import { isFullScopeAllowed } from './subscription-scope-choice/full-scope-allowed';
import { SubscriptionScope } from './subscription-scope-choice/subscription-scope-choice.component';
import { Owner, OwnerCompanyOption } from './type';

const lmpProductTypes = [ProductsTypeEnum.UNFURNISHED, ProductsTypeEnum.LMNP];
// Valeurs autorisées pour les query params du deep link (durcissement : on ignore toute valeur forgée)
const BORROWER_INSURANCE_CONTEXTS = ['NEW', 'CHANGE'];
const BORROWER_INSURANCE_MODES = ['API', 'MANUAL'];
type OptionType = Array<{ value: any; label: string }>;
@UntilDestroy()
@Component({
  selector: 'subscription-init-overview',
  templateUrl: './subscription-init-overview.component.html',
  styleUrls: ['./subscription-init-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubscriptionInitOverviewComponent
  extends EaSubscriptionBuilder
  implements OnInit
{
  _model: any = [];
  productType: ProductsTypeEnum;
  selectedItems: any = null;
  value: any;
  multiple: boolean = false;
  optionsPhysical: OptionType = [];
  owner$: Observable<OptionType>;
  selectedSubmit: string = null;
  selected: string = 'Single';
  queryParams: any;
  maxSelected: number | null = 1;
  userId: number;
  couple: any[] = [];
  loading: boolean = false;
  selectedProduct: ProductGlobalItem;
  toggle = false;
  pictureFile: string = '';
  consultantBackUrl = environment.servers.resources.url || '';
  sessionConsultant: boolean = false;
  attachedConsultant: number;
  secondConsultant: number;
  gridkyPropertyId: number = undefined;
  loanId: number = undefined;
  borrowerInsuranceContext: string = undefined;
  borrowerInsuranceMode: string = undefined;
  operation: string = 'SUBSCRIPTION';
  operationAssetContractId: number = undefined;
  private devisId: number;
  private preSelectedOwnerId: number;
  devisList: PrevoyanceDevisData[] = [];
  showDevisOrProductChoice = false;
  showDevisList = false;

  // Séquençage V2 — choix du périmètre de souscription, intercalé avant le /init.
  selectedScope: SubscriptionScope | null = null;
  showScopeStep = false;
  private pendingPledgeArgs: {
    owners: any[] | undefined;
    physicalSubscription: boolean;
  } | null = null;

  optionsCompany: FormControlOption[] = [];
  subsciptionOptions = [
    {
      label: 'Souscription individuelle',
      value: 'Single',
      suffix: 'A',
      svg: 'owner.svg',
      maxSelected: 1,
    },
    {
      label: 'Co-souscription',
      value: 'Couple',
      suffix: 'B',
      svg: 'tentant.svg',
      maxSelected: 2,
    },
  ];
  product: ProductSearchItem;
  owners: Owner[];
  ownerOptionsPhysical: OptionType;
  ownerCompany: OwnerCompanyOption[] = [];
  ownerCompanyOptions: OptionType;
  productTypes: ProductsTypeEnum[] = [
    ProductsTypeEnum.ASSURANCE_VIE,
    ProductsTypeEnum.CONTRAT_CAPI,
    ProductsTypeEnum.PEI,
    ProductsTypeEnum.PEE,
    ProductsTypeEnum.PERCO,
    ProductsTypeEnum.GIRARDIN,
    ProductsTypeEnum.CROWDFUNDING,
    ProductsTypeEnum.PER,
    ProductsTypeEnum.PRIVATE_EQUITY,
    ProductsTypeEnum.FCPI,
    ProductsTypeEnum.FPCI,
    ProductsTypeEnum.BOND,
    ProductsTypeEnum.GFV,
    ProductsTypeEnum.GFI,
    ProductsTypeEnum.CONTINGENCY,
    ProductsTypeEnum.GFF,
    ProductsTypeEnum.TITRES_PARTICIPATIFS,
    ProductsTypeEnum.SOCIAL_SHARE,
    ProductsTypeEnum.OBLIGATIONS,
    ProductsTypeEnum.DIVERSIFICATION_INVESTMENT,
    ProductsTypeEnum.COMPTE_TITRE,
    ProductsTypeEnum.PEA,
    ProductsTypeEnum.FIP,
    ProductsTypeEnum.FIP_CORSE,
    ProductsTypeEnum.FIP_DOMTOM,
    ProductsTypeEnum.UNFURNISHED,
    ProductsTypeEnum.LMNP,
    ProductsTypeEnum.CAT,
    ProductsTypeEnum.PRESTATION,
    ProductsTypeEnum.FCPR,
    ProductsTypeEnum.FPS,
    ProductsTypeEnum.BORROWER_INSURANCE,
    ProductsTypeEnum.INSURANCE,
  ];
  currentlyChecked: any;
  members: OptionType = [];
  delBin: boolean = false;
  legalEntityChoices: PledgeLegalEntityChoices | null = null;
  legalEntitySelection: LegalEntitySelection | null = null;
  sellerLegalEntitySelectOptions: Record<number, PrSelectOption[]> = {};
  attachedLegalEntitySelectOptions: PrSelectOption[] = [];
  showAttachedLegalEntitySelect = false;
  attachedLegalEntityOwner = '';
  private legalEntityChoicesLoader = new LegalEntityChoicesLoader(
    (productType, productId) => this.pledgeDao.getLegalEntityChoices(productType, productId),
    () =>
      this.notificationService.error(
        'Erreur',
        "Les entités légales n'ont pas pu être chargées : les entités par défaut seront appliquées à l'opération.",
        { timeOut: 5000 },
      ),
  );

  constructor(
    public userDao: UserDao,
    private subscriptionFacade: SubscriptionFacadeService,
    public route: ActivatedRoute,
    protected formConstructor: PrFormConstructor,
    private _router: Router,
    private cdr: ChangeDetectorRef,
    private httpApi: HttpApiService,
    private pledgeDao: PledgeDao,
  ) {
    super(formConstructor);
  }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    // productType : uniquement une valeur connue de ProductsTypeEnum, sinon ignorée
    this.productType = (Object.values(ProductsTypeEnum) as string[]).includes(
      params.productType,
    )
      ? (params.productType as ProductsTypeEnum)
      : undefined;
    this.devisId = this.parseIdParam(params.devisId);
    this.preSelectedOwnerId = this.parseIdParam(params.ownerId);
    this.loanId = this.parseIdParam(params.loanId);
    this.borrowerInsuranceContext = BORROWER_INSURANCE_CONTEXTS.includes(
      params.contexte,
    )
      ? params.contexte
      : undefined;
    this.borrowerInsuranceMode = BORROWER_INSURANCE_MODES.includes(
      params.borrowerInsuranceMode,
    )
      ? params.borrowerInsuranceMode
      : undefined;
    this.clear();
    this.initForm({});
    this.initializeOwnerStream();
    this.handleProductQueryParams();
    this.handleDevisAutoCreate();
    this.loadDevisListIfApplicable();
  }

  /**
   * Parse un identifiant reçu en query param : n'accepte qu'un entier strictement positif.
   * Rejette les valeurs forgées ('1e9', '-5', '1.5', '[object Object]', vide...).
   */
  private parseIdParam(raw: unknown): number | undefined {
    if (raw == null) return undefined;
    const str = String(raw).trim();
    if (!/^\d+$/.test(str)) return undefined;
    const value = parseInt(str, 10);
    return Number.isInteger(value) && value > 0 ? value : undefined;
  }
  handleProductQueryParams() {
    const productId = this.getProductIdFromQueryParam();
    productId && this.getProductInfo(productId);
  }
  private getProductIdFromQueryParam(): string {
    return this.route.snapshot.queryParams?.productId || '';
  }
  initializeOwnerStream(): void {
    this.owner$ = combineLatest([
      this.userDao.getCouple(),
      this.userDao.getOwners(),
      this.userDao.getAuthentified(),
      this.userDao.getCompanies(),
    ]).pipe(
      map(([couple, owners, user, company]) => {
        this.setOwnersInfo(owners);
        this.setCoupleInfo(couple, user);
        this.setConsultantPicture(user);
        this.setOptionsPhysical(couple);
        this.setOptionsCompany(company);
        this.setOwnerOptions();

        this.sessionConsultant = !!user?.sessionConsultant;
        this.attachedConsultant = user?.attachedConsultant;
        this.secondConsultant = user?.secondConsultant;

        this.initializeMembers(user?.organizationId);
        this.loadLegalEntityChoices();
        return this.optionsPhysical;
      }),
      shareReplay(1),
    );
  }
  initializeMembers(organizationId?): void {
    this.httpApi
      .get(
        API_ROUTE_ORGANIZATION_MEMBER_GLOBAL.replace(
          ':organizationId',
          organizationId || '1',
        ),
      ) // TODO replace with real organization id
      .subscribe(response => {
        this.members = response.map(member => ({
          value: member.id,
          label: member.fullName,
        }));
        this.cdr.detectChanges();
      });
  }

  getConsultantFullName(memberId: number): string {
    return this.members.find(member => member.value === memberId)?.label;
  }

  private loadLegalEntityChoices(): void {
    if (!this.sessionConsultant) {
      return;
    }
    const productId = this.selectedProduct?.id ? Number(this.selectedProduct.id) : undefined;
    this.legalEntityChoicesLoader
      .load(this.getProductType(), productId)
      .pipe(untilDestroyed(this))
      .subscribe(choices => {
        this.legalEntityChoices = choices;
        this.legalEntitySelection = choices ? defaultLegalEntitySelection(choices) : null;
        this.sellerLegalEntitySelectOptions = {};
        choices?.sellers.forEach(
          seller => (this.sellerLegalEntitySelectOptions[seller.memberId] = sellerLegalEntityOptions(choices, seller.memberId)),
        );
        this.attachedLegalEntitySelectOptions = attachedLegalEntityOptions(choices);
        this.showAttachedLegalEntitySelect = showsAttachedLegalEntitySelect(choices);
        this.attachedLegalEntityOwner = attachedLegalEntityOwnerLabel(choices);
        this.cdr.detectChanges();
      });
  }

  showSellerLegalEntitySelect(memberId: number): boolean {
    return showsSellerLegalEntitySelect(this.legalEntityChoices, memberId);
  }

  onSellerLegalEntityChange(memberId: number, event: { value: number }): void {
    this.legalEntitySelection = selectSellerLegalEntity(this.legalEntityChoices, this.legalEntitySelection, memberId, event.value);
  }

  onAttachedLegalEntityChange(event: { value: number }): void {
    this.legalEntitySelection = { ...this.legalEntitySelection, attachedLegalEntityId: event.value };
  }

  private getDisplayedSellerIds(): number[] {
    return this.secondConsultant && !this.delBin ? [this.attachedConsultant, this.secondConsultant] : [this.attachedConsultant];
  }

  setOwnersInfo(owners: Owner[]): void {
    this.owners = owners.filter(o => o.current);
  }
  setCoupleInfo(couple: UserAuthentified[], user: UserAuthentified): void {
    this.couple = couple.map(c => c.id);
    if (this.couple.length < 2) {
      this.subsciptionOptions.splice(1);
    }
  }
  setConsultantPicture(user: UserAuthentified): void {
    const url = user?.sessionConsultant?.consultantProfilePictureUrl;
    this.pictureFile = url ? this.consultantBackUrl + url : '';
    this.userId = user.id;
  }
  setOptionsPhysical(couple: UserAuthentified[]): void {
    let leftowners = this.owners;
    for (let user of couple) {
      const owner = leftowners.find(o => o.id == user.id);
      leftowners = leftowners.filter(o => o !== owner);
      this.addOptionPhysical(owner);
    }
    for (let owner of leftowners) {
      if (owner['relation'] === 'OTHER_USER') {
        this.addOptionPhysical(owner);
      }
    }
  }
  private addOptionPhysical(owner: Owner): void {
    const option = {
      value: this.multiple ? [owner.id] : owner.id,
      label: owner.label,
    };
    this.optionsPhysical.push(option);
  }
  setOptionsCompany(company: any[]): void {
    if (company?.length) {
      this.ownerCompany = [...company];
      this.optionsCompany = company.map(c => ({
        value: c.id,
        label: c.label + ' ' + c.legalForm,
      }));
    }
  }
  setOwnerOptions(): void {
    this.optionsPhysical.push({
      value: null,
      label: 'Autre',
    });
    this.optionsCompany.push({
      value: null,
      label: 'Autre',
    });
    this.ownerOptionsPhysical = this.optionsPhysical;
    this.ownerCompanyOptions = this.optionsCompany;
  }

  get showQuestionSeller(): boolean {
    return this.showQuestionDescription && this.sessionConsultant;
  }
  get showQuestionDescription(): boolean {
    const { productType, selectedProduct, selectedSubmit, productTypes } = this;

    return Boolean(
      !selectedSubmit &&
      ((productTypes.includes(productType) && selectedProduct) ||
        productTypes.every(type => type !== productType)),
    );
  }
  get showProductSelection(): boolean {
    const productId = this.getProductIdFromQueryParam();
    const { productType, selectedProduct, productTypes } = this;
    if (productId) return false;
    if (this.showDevisOrProductChoice || this.showDevisList) return false;
    return productTypes.some(type => type === productType) && !selectedProduct;
  }
  selectOption(option: FormControlOption) {
    this.selected = option.value as string;
    this.selected == 'Single' ? (this.maxSelected = 1) : (this.maxSelected = 2);
  }
  onSubmitSelected() {
    this.selectedSubmit = this.selected || 'Single';
    if (this.maxSelected === 2) {
      this.couple.length == 2 && this.createPledge([...this.couple]);
    }
  }
  onSubmit() {
    const physicalOwnerIds = this.form.value?.physicalOwnerIds;
    const companyOwnerIds = this.form.value?.companyOwnerIds;
    if (physicalOwnerIds?.length || companyOwnerIds?.length) {
      const ids = physicalOwnerIds?.length ? physicalOwnerIds : companyOwnerIds;
      const physicalSubscription = physicalOwnerIds?.length > 0;
      this.createPledge(ids, physicalSubscription);
    }
  }

  /**
   * Séquençage V2 — Point de convergence commun (souscription classique + opérations).
   * On intercale d'abord le choix de scope ; la création effective du pledge est faite par
   * {@link doCreatePledge} une fois le scope confirmé.
   */
  createPledge(
    owners: any[] = undefined,
    physicalSubscription: boolean = true,
  ) {
    this.pendingPledgeArgs = { owners, physicalSubscription };
    this.loading = false;
    this.showScopeStep = true;
    this.cdr.detectChanges();
  }

  /** Séquençage V2 — Le bouton « précédent » de l'écran scope est masqué pour les opérations
   * (init direct sans écran amont). */
  get showScopePrevious(): boolean {
    return !this.isOperationTypeWithCurrentContrat(this.operation);
  }

  get fullScopeAllowed(): boolean {
    return isFullScopeAllowed(this.getProductType(), this.selectedProduct, this.operation);
  }

  /** Séquençage V2 — Scope confirmé → création effective du pledge. */
  onScopeSelected(scope: SubscriptionScope) {
    this.selectedScope = scope;
    this.showScopeStep = false;
    const args = this.pendingPledgeArgs;
    this.pendingPledgeArgs = null;
    this.doCreatePledge(args?.owners, args?.physicalSubscription ?? true);
  }

  /** Séquençage V2 — Retour depuis l'écran scope vers l'étape précédente du parcours. */
  onScopePrev() {
    this.showScopeStep = false;
    this.pendingPledgeArgs = null;
    this.cdr.detectChanges();
  }

  private doCreatePledge(
    owners: any[] = undefined,
    physicalSubscription: boolean = true,
  ) {
    this.loading = true;
    const {
      productType,
      ownerIds,
      productIds,
      productName,
      gridkyPropertyId,
      loanId,
      borrowerInsuranceContext,
      borrowerInsuranceMode,
      sellerIds,
      operation,
      operationAssetContractId,
    } = this.getProductDetails(owners);
    const createPledgeSubscription = this.initPledgeSubscription(
      productType,
      productName,
      ownerIds,
      productIds,
      sellerIds,
      physicalSubscription,
      gridkyPropertyId,
      loanId,
      borrowerInsuranceContext,
      borrowerInsuranceMode,
      operation,
      operationAssetContractId,
    );
    this.handlePledgeCreationSubscription(
      createPledgeSubscription,
      productType,
      ownerIds,
    );
  }
  private handlePledgeCreationSubscription(
    createPledgeSubscription: Observable<PledgeRead>,
    productType: ProductsTypeEnum,
    ids: any,
  ) {
    createPledgeSubscription.subscribe(
      pledge => {
        this.handlePledgeCreationSuccess(productType, pledge, ids);
      },
      error => {
        this.handlePledgeCreationError(error);
      },

      () => {
        this.loading = false;
      },
    );
  }

  private initPledgeSubscription(
    productType: ProductsTypeEnum,
    productName: string,
    ownerIds: number[],
    productIds: number[],
    sellerIds: number[],
    physicalSubscription: boolean,
    gridkyPropertyId: number = undefined,
    loanId: number = undefined,
    borrowerInsuranceContext: string = undefined,
    borrowerInsuranceMode: string = undefined,
    operation: string = 'SUBSCRIPTION',
    operationAssetContractId: number = undefined,
  ) {
    const params = {
      productType,
      productName,
      ownerIds: ownerIds,
      productIds,
      sellerIds,
      physicalUser: physicalSubscription,
      gridkyPropertyId,
      loanId,
      borrowerInsuranceContext,
      borrowerInsuranceMode,
      operation,
      operationAssetContractId,
      // Séquençage V2 — périmètre choisi par le CGP avant l'init.
      scope: this.selectedScope,
      ...toLegalEntityInitParams(this.legalEntityChoices, this.legalEntitySelection, this.getDisplayedSellerIds()),
    };
    return this.subscriptionFacade.initPledge(params).pipe(
      untilDestroyed(this),
      catchError(error => {
        this.handlePledgeCreationError(error);
        console.error(error);
        return EMPTY;
      }),
    );
  }
  private getProductDetails(owners: any[] | undefined) {
    const productType = this.getProductType();
    const ownerIds = this.getOwnerIds(owners);
    const productIds = this.getProductIds(productType);
    const productName = this.getProductName(productType);
    const gridkyPropertyId = this.getGridkyPropertyId();
    const loanId = this.getLoanId();
    const borrowerInsuranceContext = this.getBorrowerInsuranceContext();
    const borrowerInsuranceMode = this.getBorrowerInsuranceMode();
    const sellerIds = this.getSellerIds();
    const operation = this.getOperation();
    const operationAssetContractId = this.getOperationAssetContractId();
    return {
      productType,
      ownerIds,
      productIds,
      productName,
      gridkyPropertyId,
      loanId,
      borrowerInsuranceContext,
      borrowerInsuranceMode,
      sellerIds,
      operation,
      operationAssetContractId,
    };
  }
  private getGridkyPropertyId() {
    return this.gridkyPropertyId;
  }

  private getLoanId() {
    return this.loanId;
  }

  private getBorrowerInsuranceContext() {
    return this.borrowerInsuranceContext;
  }

  private getBorrowerInsuranceMode() {
    return this.borrowerInsuranceMode;
  }

  private getOperation(): string {
    return this.operation;
  }

  private getOperationAssetContractId(): number | undefined {
    return this.operationAssetContractId;
  }

  private getProductType(): ProductsTypeEnum {
    let queryParams = this.route.snapshot.queryParams.productType;
    let productType = this.mapProductType(queryParams);
    if (this.isLmpProductType(productType)) {
      productType = this.getProductTypeFromObject(productType);
    }
    return productType;
  }
  private getOwnerIds(owners: any[] | undefined): number[] {
    return owners || this.form.value.physicalOwnerIds;
  }
  private getSellerIds(): number[] {
    if (this.delBin) {
      return [this.attachedConsultant];
    } else return [];
  }
  private getProductIds(productType: ProductsTypeEnum): number[] | undefined {
    const isScpiProduct = productType === ProductsTypeEnum.SCPI;
    return isScpiProduct ? undefined : [Number(this.selectedProduct?.id)];
  }

  private getProductName(productType: ProductsTypeEnum): string | undefined {
    const isScpiProduct = productType === ProductsTypeEnum.SCPI;
    return isScpiProduct
      ? undefined
      : (this.selectedProduct?.name ?? 'Euodia Vie');
  }
  private mapProductType(queryParams: any): ProductsTypeEnum {
    return mapProductType(queryParams);
  }
  private isLmpProductType(productType: ProductsTypeEnum): boolean {
    return lmpProductTypes.includes(productType);
  }
  private getProductTypeFromObject(
    defaultProductType: ProductsTypeEnum,
  ): ProductsTypeEnum {
    return (
      (this.selectedProduct?.type as ProductsTypeEnum) || defaultProductType
    );
  }
  private handlePledgeCreationSuccess(
    productType: ProductsTypeEnum,
    pledge: PledgeRead,
    ownerIds: any[],
  ) {
    this.loading = false;
    if (productType) {
      if (lmpProductTypes.includes(productType)) {
        productType = (pledge?.productType as ProductsTypeEnum) || productType;
      }
      const pledgeId = pledge.id;
      const additionalQueryParams: Record<string, string | number> = {};
      if (this.devisId) additionalQueryParams.devisId = this.devisId;
      if (this.preSelectedOwnerId) additionalQueryParams.ownerId = this.preSelectedOwnerId;

      this.subscriptionFacade.navigateTo(
        reverseMapProductType(productType as keyof typeof ProductsTypeEnum),
        pledgeId,
        ownerIds,
        undefined,
        additionalQueryParams,
      );
    }
  }

  private handlePledgeCreationError(error?: any) {
    const errorMessage = error?.error?.errors?.length
      ? error.error.errors.join(', ')
      : null;

    this.loading = false;
    this.cdr.detectChanges();
    this.notificationService.error(
      'Erreur',
      errorMessage ||
        "Une erreur est survenue lors de la création de l'operation, veuillez contacter le support.",
      {
        timeOut: 5000,
      },
    );
  }

  prev() {
    const productId = this.getProductIdFromQueryParam();
    if (productId) return;
    this.selectedSubmit = null;
    this.selected = null;
    this.form.reset();
    this.selectedProduct = null;
    this.delBin = false;
  }
  setQueryParam(param) {
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        type: param,
      },
      queryParamsHandling: 'merge',
    });
  }
  selectCheckBox(targetType: any) {
    if (this.currentlyChecked === targetType) {
      this.currentlyChecked = 'NONE';
      return;
    }
    this.currentlyChecked = targetType;
  }
  saveProduct($event: {
    product: ProductGlobalItem;
    gridkyPropertyId: number;
    loanId: number;
    borrowerInsuranceContext: string;
    borrowerInsuranceMode: string;
    operation: string;
    operationAssetContractId: number;
  }) {
    this.selectedProduct = $event.product;
    this.gridkyPropertyId = $event.gridkyPropertyId;
    this.loanId = $event.loanId;
    this.borrowerInsuranceContext = $event.borrowerInsuranceContext;
    this.borrowerInsuranceMode = $event.borrowerInsuranceMode;
    this.operation = $event.operation;
    this.operationAssetContractId = $event.operationAssetContractId;
    const isOperation = this.isOperationTypeWithCurrentContrat(this.operation);
    const onProductLoaded = isOperation ? () => this.createPledge() : undefined;
    if (isOperation) this.loading = true;
    if (this.selectedProduct?.id) this.getProductInfo(this.selectedProduct.id, onProductLoaded);
    else onProductLoaded?.();
    if (
      [ProductsTypeEnum.CAT, ProductsTypeEnum.CONTINGENCY, ProductsTypeEnum.INSURANCE].includes(
        this.productType,
      ) ||
      isOperation
    )
      this.selectedSubmit = this.selected || 'Single';
  }

  private isOperationTypeWithCurrentContrat(operation: string): boolean {
    return !['SUBSCRIPTION', 'TRANSFER', 'ORDRE_DE_REMPLACEMENT'].includes(
      operation,
    );
  }

  getProductInfo(id: any, onLoaded?: () => void) {
    return this.httpApi
      .get(API_ROUTE_PRODUCT.replace(':id', id))
      .pipe(take(1))
      .subscribe({
        next: (product: ProductSearchItem) => {
          this.selectedProduct = product as any;
          this.setPhysicalOptions(product);

          this.setCompanyOptions(product);
          this.loadLegalEntityChoices();
          this.cdr.detectChanges();
          onLoaded?.();
        },
        error: () => onLoaded?.(),
      });
  }
  private setCompanyOptions(product: ProductSearchItem) {
    const ownerCompanyOptions: OwnerCompanyOption[] = [];
    const ownerCompany = [...this.ownerCompany];
    if (product.subscriptionMoralEntityAllowed == true) {
      ownerCompanyOptions.push(...ownerCompany);
      ownerCompanyOptions.push({ id: null, label: 'Autre' });
    }
    this.ownerCompanyOptions = ownerCompanyOptions.map(o => ({
      value: o.id,
      label: o.label,
    }));
  }
  private setPhysicalOptions(product: ProductSearchItem) {
    this.product = product;
    const ownerOptions: Owner[] = [];
    const ownerAndSpouse = this.filterOwnersByCouple(
      this?.owners,
      this?.couple,
    );
    const ownerChild = this.filterOwnersByRelation(this?.owners);
    ownerOptions.push(...ownerAndSpouse);
    if (product?.subscriptionChildAllowed) {
      this.addOwnerChildAndAutre(ownerOptions, ownerChild);
    } else if (!this.couple?.length) {
      this.addAutreOption(ownerOptions);
    }
    this.ownerOptionsPhysical = this.createOwnerOptions(ownerOptions);
  }
  private filterOwnersByCouple(
    owners: Owner[],
    couple: number[] | undefined,
  ): Owner[] {
    return owners?.filter(o => couple?.includes(o.id) ?? false) || [];
  }
  private filterOwnersByRelation(owners: Owner[]): Owner[] {
    return (
      owners?.filter(
        o => !this.couple?.includes(o.id) && o?.relation !== 'COMPANY',
      ) || []
    );
  }
  private createOwnerOptions(
    owners: Owner[],
  ): { value: number; label: string }[] {
    return owners.map(o => ({ value: o.id, label: o.label }));
  }
  private addOwnerChildAndAutre(
    ownerOptions: Owner[],
    ownerChild: Owner[],
  ): void {
    ownerOptions.push(...ownerChild);
    this.addAutreOption(ownerOptions);
  }
  private addAutreOption(ownerOptions: Owner[]): void {
    ownerOptions.push({ id: null, label: 'Autre' });
  }
  clear() {
    this.subscriptionFacade.clearPledge();
    this.subscriptionFacade.clearStep();
  }
  deleteBinome() {
    //this.secondConsultant = null;
    this.delBin = true;
  }

  /**
   * Chemin A : devisId + ownerId + productId dans l'URL (depuis albr-pro).
   * On charge le produit puis on cree le pledge directement
   * sans afficher product-init ni owner-selection.
   */
  private handleDevisAutoCreate(): void {
    const productId = this.getProductIdFromQueryParam();
    if (!this.devisId || !this.preSelectedOwnerId || !productId) return;

    // Afficher le loader pendant le chargement du produit + creation du pledge
    this.loading = true;

    // Charger le produit (meme appel que getProductInfo) puis creer le pledge
    this.httpApi
      .get(API_ROUTE_PRODUCT.replace(':id', productId))
      .pipe(take(1))
      .subscribe((product: ProductSearchItem) => {
        this.selectedProduct = product as any;
        // Pour AE venant d'un devis albr-pro : le mode est MANUAL par defaut
        // (le devis contient deja les tarifs, pas besoin de regénérer via API)
        if (this.productType === 'BORROWER_INSURANCE' && !this.borrowerInsuranceMode) {
          this.borrowerInsuranceMode = 'MANUAL';
        }
        this.createPledge([this.preSelectedOwnerId], true);
      });
  }

  /**
   * Chemin B : productType=CONTINGENCY sans devisId ni productId.
   * Charge la liste des devis du client pour proposer le choix devis/produit.
   * Si aucun devis → showProductSelection s'affiche directement (flux standard).
   */
  private static readonly DEVIS_ENDPOINT_BY_PRODUCT_TYPE: Partial<Record<ProductsTypeEnum, string>> = {
    [ProductsTypeEnum.CONTINGENCY]: '/prevoyance/prevoyance-personnelle/devis',
    [ProductsTypeEnum.INSURANCE]: '/prevoyance/mutuelle/devis',
  };

  private loadDevisListIfApplicable(): void {
    const devisPath = SubscriptionInitOverviewComponent.DEVIS_ENDPOINT_BY_PRODUCT_TYPE[this.productType];
    if (
      !devisPath ||
      this.getProductIdFromQueryParam() ||
      this.devisId
    ) return;

    this.userDao.getAuthentified().pipe(
      take(1),
      switchMap(user =>
        this.httpApi.get(`/accounts/${user.accountId}${devisPath}`),
      ),
      catchError(() => of([] as PrevoyanceDevisData[])),
      untilDestroyed(this),
    ).subscribe((devis: PrevoyanceDevisData[]) => {
      this.devisList = devis || [];
      this.showDevisOrProductChoice = this.devisList.length > 0;
      this.cdr.detectChanges();
    });
  }

  onSelectFromDevis(): void {
    this.showDevisOrProductChoice = false;
    this.showDevisList = true;
  }

  onSelectFromProduct(): void {
    this.showDevisOrProductChoice = false;
    // showProductSelection redevient true car showDevisOrProductChoice et showDevisList sont false
  }

  onDevisSelected(devis: PrevoyanceDevisData): void {
    // Setter devisId et ownerId pour la propagation dans handlePledgeCreationSuccess (Phase 7)
    this.devisId = devis.id;
    this.preSelectedOwnerId = devis.ownerId;

    this.selectedProduct = { id: devis.productId, name: devis.productName } as any;
    this.loading = true;
    this.httpApi
      .get(API_ROUTE_PRODUCT.replace(':id', String(devis.productId)))
      .pipe(take(1))
      .subscribe({
        next: (product: ProductSearchItem) => {
          this.selectedProduct = product as any;
          this.createPledge([devis.ownerId], true);
        },
        error: () => this.createPledge([devis.ownerId], true),
      });
  }
}
